import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { reduzirImagem } from '@/lib/imagem';
import ImagemProduto from '@/components/feature/ImagemProduto';
import type { ImagemDisponivel } from '../types';

// Seletor de imagem de um produto já criado. Chama `loja_definir_imagem`
// imediatamente: escolher desenho, subir foto (comprimida em canvas) ou remover.
// Só faz sentido em edição (o produto já tem id).

interface Props {
  eventoId: number;
  produtoId: number;
  imagemUrl: string | null;
  imagemSlug: string | null;
  emoji: string | null;
  nome: string;
  aoMudarImagem: (url: string | null, slug: string | null) => void;
}

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 5 * 1024 * 1024;
const TAMANHO_ALVO = 2 * 1024 * 1024;

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SeletorImagemProduto({
  eventoId,
  produtoId,
  imagemUrl,
  imagemSlug,
  emoji,
  nome,
  aoMudarImagem,
}: Props) {
  const [mostrarDesenhos, setMostrarDesenhos] = useState(false);
  const [desenhos, setDesenhos] = useState<ImagemDisponivel[]>([]);
  const [nomesCategoria, setNomesCategoria] = useState<Map<string, string>>(new Map());
  const [carregandoDesenhos, setCarregandoDesenhos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroTecnico, setErroTecnico] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const [tamanhoConvertido, setTamanhoConvertido] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function carregarDesenhos() {
    setCarregandoDesenhos(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('loja_imagens_disponiveis');
      if (error) throw error;
      setDesenhos((data as ImagemDisponivel[]) || []);
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setCarregandoDesenhos(false);
    }
  }

  // Busca os nomes amigáveis das categorias (não fatal: sem elas, usa a chave crua).
  useEffect(() => {
    if (desenhos.length === 0) return;
    let ativo = true;
    supabase
      .from('loja_categorias')
      .select('codigo, nome')
      .then(({ data, error }) => {
        if (error || !ativo) return;
        const mapa = new Map<string, string>();
        ((data as { codigo: string; nome: string }[]) || []).forEach((c) =>
          mapa.set(c.codigo, c.nome),
        );
        setNomesCategoria(mapa);
      });
    return () => {
      ativo = false;
    };
  }, [desenhos]);

  function alternarDesenhos() {
    const vaiMostrar = !mostrarDesenhos;
    setMostrarDesenhos(vaiMostrar);
    if (vaiMostrar && desenhos.length === 0) carregarDesenhos();
  }

  async function copiarErro() {
    if (!erroTecnico) return;
    try {
      await navigator.clipboard.writeText(erroTecnico);
      setRecado('Erro copiado.');
    } catch {
      setRecado('Não foi possível copiar. Selecione o texto manualmente.');
    }
  }

  async function escolherDesenho(slug: string) {
    setEnviando(true);
    setErro(null);
    setErroTecnico(null);
    setRecado(null);
    try {
      const { error } = await supabase.rpc('loja_definir_imagem', {
        p_produto: produtoId,
        p_imagem_slug: slug,
        p_imagem_url: null,
      });
      if (error) {
        setErroTecnico(mensagemAmigavel(error, 'Erro ao escolher o desenho.'));
        return;
      }
      aoMudarImagem(null, slug);
      setRecado('Desenho escolhido.');
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  async function aoEscolherFoto(arquivo: File | null) {
    setErro(null);
    setErroTecnico(null);
    setRecado(null);
    setTamanhoConvertido(null);
    if (!arquivo) return;
    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Esta imagem tem mais de 5 MB. Escolha uma menor.');
      return;
    }
    setEnviando(true);
    try {
      // Converte para webp (800 px) e, se ainda passar de 2 MB, reduz a
      // qualidade até caber — para o Storage aceitar sem estourar o limite.
      let qualidade = 0.82;
      let reduzido = await reduzirImagem(arquivo, 800, qualidade, 'image/webp');
      while (reduzido.size > TAMANHO_ALVO && qualidade > 0.4) {
        qualidade -= 0.1;
        reduzido = await reduzirImagem(arquivo, 800, qualidade, 'image/webp');
      }
      setTamanhoConvertido(formatarTamanho(reduzido.size));

      const caminho = `produtos/${eventoId}/${produtoId}-${Date.now()}.webp`;
      const { error: errUpload } = await supabase.storage.from('capas').upload(caminho, reduzido);
      if (errUpload) {
        setErroTecnico(mensagemAmigavel(errUpload, 'Erro ao enviar a foto.'));
        return;
      }
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      const url = data.publicUrl;
      const { error } = await supabase.rpc('loja_definir_imagem', {
        p_produto: produtoId,
        p_imagem_slug: imagemSlug ?? null,
        p_imagem_url: url,
      });
      if (error) {
        setErroTecnico(mensagemAmigavel(error, 'Erro ao salvar a imagem.'));
        return;
      }
      aoMudarImagem(url, imagemSlug);
      setRecado('Foto salva. Ela aparece no lugar do desenho.');
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function removerFoto() {
    setEnviando(true);
    setErro(null);
    setErroTecnico(null);
    setRecado(null);
    try {
      const { error } = await supabase.rpc('loja_definir_imagem', {
        p_produto: produtoId,
        p_imagem_slug: imagemSlug ?? null,
        p_imagem_url: null,
      });
      if (error) throw error;
      aoMudarImagem(null, imagemSlug);
      setRecado('Imagem removida.');
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
    }
  }

  const grupos = useMemo(() => {
    const ordem: string[] = [];
    const mapa = new Map<string, ImagemDisponivel[]>();
    for (const d of desenhos) {
      const rotulo = nomesCategoria.get(d.categoria) || d.categoria;
      if (!mapa.has(rotulo)) {
        mapa.set(rotulo, []);
        ordem.push(rotulo);
      }
      mapa.get(rotulo)!.push(d);
    }
    return ordem.map((rotulo) => ({ rotulo, itens: mapa.get(rotulo)! }));
  }, [desenhos, nomesCategoria]);

  return (
    <div className="rounded-md border border-secondary-200 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-base font-medium text-foreground-950">
            Imagem do produto
          </span>
          <p className="mt-1 text-sm text-secondary-500">
            Aparece na vitrine e na barraca. Sem foto, vale o desenho do cardápio.
          </p>
        </div>
        <ImagemProduto
          imagemUrl={imagemUrl}
          imagemSlug={imagemSlug}
          emoji={emoji}
          nome={nome}
          tamanho="miniatura"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={alternarDesenhos}
          disabled={enviando}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-gallery-line" />
          </span>
          Escolher desenho
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={enviando}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-camera-line" />
          </span>
          Enviar foto
        </button>
        {imagemUrl && (
          <button
            type="button"
            onClick={removerFoto}
            disabled={enviando}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-danger-600 transition-colors hover:bg-danger-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-delete-bin-line" />
            </span>
            Remover foto
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={(e) => aoEscolherFoto(e.target.files?.[0] ?? null)}
        />
      </div>

      {enviando && (
        <p className="mt-2 flex items-center gap-2 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          Salvando imagem…
        </p>
      )}

      {tamanhoConvertido && !erroTecnico && (
        <p className="mt-2 text-sm text-secondary-500">
          Arquivo convertido: {tamanhoConvertido}.
        </p>
      )}

      {recado && <p className="mt-2 text-sm text-accent-700">{recado}</p>}

      {erro && (
        <p className="mt-2 text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}

      {erroTecnico && (
        <div className="mt-3 rounded-md border border-danger-200 bg-danger-50 p-3" role="alert">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-danger-700">
                Não foi possível salvar a imagem.
              </p>
              <p className="mt-1 break-words text-sm text-danger-700">{erroTecnico}</p>
            </div>
            <button
              type="button"
              onClick={copiarErro}
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-danger-300 bg-white px-2 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-file-copy-line" />
              </span>
              Copiar erro
            </button>
          </div>
        </div>
      )}

      {mostrarDesenhos && (
        <div className="mt-4 rounded-lg border border-secondary-200 bg-background-50 p-4">
          {carregandoDesenhos ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-secondary-100" />
              ))}
            </div>
          ) : grupos.length === 0 ? (
            <p className="py-4 text-center text-sm text-secondary-500">
              Nenhum desenho disponível.
            </p>
          ) : (
            <div className="max-h-[320px] space-y-4 overflow-y-auto pr-1">
              {grupos.map((g) => (
                <div key={g.rotulo}>
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    {g.rotulo}
                  </h5>
                  <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-6">
                    {g.itens.map((d) => {
                      const ativo = imagemSlug === d.slug;
                      return (
                        <button
                          key={d.slug}
                          type="button"
                          onClick={() => escolherDesenho(d.slug)}
                          title={d.rotulo}
                          aria-pressed={ativo}
                          className={`flex cursor-pointer flex-col items-center gap-1 rounded-md border p-2 transition-colors ${
                            ativo
                              ? 'border-primary-500 bg-primary-50/50'
                              : 'border-secondary-200 hover:border-secondary-300 hover:bg-background-100'
                          }`}
                        >
                          <ImagemProduto
                            imagemUrl={null}
                            imagemSlug={d.slug}
                            emoji={null}
                            nome={d.rotulo}
                            tamanho="miniatura"
                          />
                          <span className="w-full truncate text-center text-xs leading-tight text-foreground-700">
                            {d.rotulo}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}